import {
    CalculatorInputFieldsType,
    CalculatorInputOptions,
} from './CalculatorInputValue';
import { CalculatorOutputType } from '../../../shared/classes/CalculatorOutputParams';
import { CalculatorHTMMode } from '../../../shared/classes/CalculatorMode';
import { calculatorHTMItemValues } from '../../../shared/constants/calculator_htm_item.constant';
import { CalculatorConfig } from './CalculatorConfig';

export class CalculatorOutputFields {

    constructor(private readonly d: CalculatorInputFieldsType,
                private readonly calculatorMode: CalculatorHTMMode) {
    }

    private config = new CalculatorConfig(this.d);

    private g(key: keyof CalculatorInputFieldsType): number {
        const formatValueFunction = CalculatorInputOptions[key]?.formatValueFunction;
        const value = +(this.d[key] || 0);

        return formatValueFunction?.(value) || value;
    }

    private I8 = this.g('average_number_scans');
    private I10 = this.g('average_rembursement');
    private I17 = this.g('facility_opening_hours');
    private I19 = this.g('facility_opening_days');
    private I21 = this.g('percent_exam_event');

    private R8 = this.g('radiologist');
    private R10 = this.g('radiographer');
    private R14 = this.g('inHouse_engineer_cost');
    private R16 = this.g('rate_HTM_org_performance') as typeof calculatorHTMItemValues[number]['code'];

    private U8 = this.g('admin_fee_rescheduledPatient');
    private U10 = this.g('radiographer_wages_per_hour');
    private U12 = this.g('radiologist_wages_per_hour');
    private U17 = this.g('radiographer_overtime_wages_percent');
    private U19 = this.g('radiologist_overtime_wages_percent');

    private K27 = this.g('avg_transfer_rate_when_system_is_down');
    private K29 = this.g('transportation_fee_for_a_patient_transfer');
    private K31 = this.g('avg_transfer_time_including_exam_time_in_hours');
    private K35 = this.g('hospital_doctor_value');
    private K37 = this.g('hospital_doctor_wages_per_hour');
    private K39 = this.g('hospital_nurses');
    private K41 = this.g('hospital_nurses_wages_per_hour');

    private V27 = this.g('avg_surgeries_requiring_diagnostic_imaging_prior_to_surgery');
    private V29 = this.g('dpt_surgeon');
    private V31 = this.g('surgeon_avg_wages_per_hour');
    private V33 = this.g('dpt_doctor_value');
    private V35 = this.g('dpt_doctor_avg_wages_per_hour');
    private V37 = this.g('dpt_nurses');
    private V39 = this.g('dpt_nurses_wages_per_hour');
    private V41 = this.g('avg_surgery_length_in_hours');
    private V43 = this.g('avg_patient_intensive_care_length_in_hours');
    private V45 = this.g('avg_cost_per_hour_in_the_intensive_care_dept');

    private average_number_exam_hour = this.I8 / this.I17;
    private yearly_technologist_radiologist_wages = this.I17 * (this.R8 * this.U12 + this.R10 * this.U10) * this.I19;
    private average_number_exam_year = this.I8 * this.I19;
    private estimated_revenue_year = this.average_number_exam_year * this.I10;

    /** OnWatch Predict Simple **/
    private on_watch_predict_examinations_saved = Math.ceil((this.config.cell_I15() * this.average_number_exam_hour) * (1 - this.I21));
    private on_watch_predict_revenue_saved = this.on_watch_predict_examinations_saved * this.I10;
    private on_watch_predict_operating_hours_saved = this.config.cell_I15();
    private on_watch_predict_staff_iddle_time_cost_avoidance = this.on_watch_predict_operating_hours_saved * (this.R8 * this.U12 + this.R10 * this.U10);
    private on_watch_predict_staff_overtime_cost_avoidance = ((this.on_watch_predict_examinations_saved * this.I21) / this.average_number_exam_hour) * (this.R8 * this.U12 * (1 + this.U19) + this.R10 * this.U10 * (1 + this.U17))
    private on_watch_predict_average_admin_fee_costs_avoidance_for_rescheduling_exams = this.on_watch_predict_examinations_saved * this.U8;
    private on_watch_predict_labor_productivity_in_hours = this.calculatorMode === CalculatorHTMMode.Direct ? 0 : this.config.cell_K35()
    private on_watch_predict_labor_productivity = this.on_watch_predict_labor_productivity_in_hours * this.R14;
    private on_watch_predict_estimated_benefits = this.on_watch_predict_revenue_saved
        + this.on_watch_predict_staff_iddle_time_cost_avoidance
        + this.on_watch_predict_staff_overtime_cost_avoidance
        + this.on_watch_predict_average_admin_fee_costs_avoidance_for_rescheduling_exams
        + (this.calculatorMode === CalculatorHTMMode.HTM ? this.on_watch_predict_labor_productivity : 0);

    /** OnWatch Predict Advanced **/
    private on_watch_predict_examinations_transfert_saved = Math.ceil(this.on_watch_predict_examinations_saved * this.K27);
    private on_watch_predict_examinations_transfert_cost_saved = this.on_watch_predict_examinations_transfert_saved * this.K29
        + this.on_watch_predict_examinations_transfert_saved
        * this.K31 * ((this.K35 * this.K37) + (this.K39 * this.K41))
        + this.on_watch_predict_examinations_transfert_saved * this.I10;
    private on_watch_predict_surgery_dpt_cost_saved = Math.ceil(this.on_watch_predict_examinations_saved * this.V27)
        * this.V41 * ((this.V29 * this.V31) + (this.V33 * this.V35) + (this.V37 * this.V39))
    private on_watch_predict_intensive_care_dpt_cost_saved = Math.ceil(this.on_watch_predict_examinations_saved * this.V27) * this.V43 * this.V45
    private on_watch_predict_patient_transfer_surgery_dpt_benefits = this.on_watch_predict_examinations_transfert_cost_saved
        + this.on_watch_predict_surgery_dpt_cost_saved
        + this.on_watch_predict_intensive_care_dpt_cost_saved;
    private on_watch_predict_total_hospital_estimated_benefits = this.on_watch_predict_patient_transfer_surgery_dpt_benefits + this.on_watch_predict_estimated_benefits;

    /** OnWatch Simple **/
    private on_watch_examinations_saved = Math.ceil((this.config.cell_L15() * this.average_number_exam_hour) * (1 - this.I21));
    private on_watch_revenue_saved = this.on_watch_examinations_saved * this.I10;
    private on_watch_operating_hours_saved = this.config.cell_L15()
    private on_watch_staff_iddle_time_cost_avoidance = this.on_watch_operating_hours_saved * (this.R8 * this.U12 + this.R10 * this.U10);
    private on_watch_staff_overtime_cost_avoidance = ((this.on_watch_examinations_saved * this.I21) / this.average_number_exam_hour) * (this.R8 * this.U12 * (1 + this.U19) + this.R10 * this.U10 * (1 + this.U17));
    private on_watch_average_admin_fee_costs_avoidance_for_rescheduling_exams = (this.on_watch_examinations_saved * this.U8);
    private on_watch_labor_productivity_in_hours = this.calculatorMode === CalculatorHTMMode.Direct ? 0 : this.config.cell_L35()
    private on_watch_labor_productivity = this.on_watch_labor_productivity_in_hours * this.R14;
    private on_watch_estimated_benefits = this.on_watch_revenue_saved
        + this.on_watch_staff_iddle_time_cost_avoidance
        + this.on_watch_staff_overtime_cost_avoidance
        + this.on_watch_average_admin_fee_costs_avoidance_for_rescheduling_exams
        + (this.calculatorMode === CalculatorHTMMode.HTM ? this.on_watch_labor_productivity : 0);

    /** OnWatch Advanced **/
    private on_watch_examinations_transfert_saved = Math.trunc(this.on_watch_examinations_saved * this.K27);
    private on_watch_examinations_transfert_cost_saved = this.on_watch_examinations_transfert_saved * this.K29
        + this.on_watch_examinations_transfert_saved * this.K31 * ((this.K35 * this.K37) + (this.K39 * this.K41))
        + this.on_watch_examinations_transfert_saved * this.R10
        + this.on_watch_examinations_transfert_saved * this.I10;
    private on_watch_surgery_dpt_cost_saved = Math.ceil(this.on_watch_examinations_saved * this.V27) * this.V41 * (
        (this.V29 * this.V31) + (this.V33 * this.V35) + (this.V37 * this.V39)
    );
    private on_watch_intensive_care_dpt_cost_saved = Math.ceil(this.on_watch_examinations_saved * this.V27) * this.V43 * this.V45
    private on_watch_patient_transfer_surgery_dpt_benefits = this.on_watch_examinations_transfert_cost_saved
        + this.on_watch_surgery_dpt_cost_saved
        + this.on_watch_intensive_care_dpt_cost_saved;
    private on_watch_total_hospital_estimated_benefits = this.on_watch_patient_transfer_surgery_dpt_benefits + this.on_watch_estimated_benefits;

    /** Break and Fix Simple **/
    private break_fix_exams_loss = Math.ceil(this.config.cell_K15() * this.average_number_exam_hour * (1 - this.I21));
    private break_fix_revenue_loss = this.break_fix_exams_loss * this.I10;
    private break_fix_operating_hours_lost = this.config.cell_K15()
    private break_fix_staff_cost_due_to_iddle_time = this.break_fix_operating_hours_lost * (this.R8 * this.U12 + this.R10 * this.U10);
    private break_fix_staff_overtime_cost = ((this.break_fix_exams_loss * this.I21) / this.average_number_exam_hour) * (this.R8 * this.U12 * (1 + this.U19) + this.R10 * this.U10 * (1 + this.U17));
    private break_fix_average_admin_fee_costs_for_rescheduling_exams = this.break_fix_exams_loss * this.U8;
    private break_fix_HTM_productivity_loss_in_hours = 0;
    private break_fix_HTM_productivity_loss = 0;
    private break_fix_estimated_Loss = this.break_fix_revenue_loss
        + this.break_fix_staff_cost_due_to_iddle_time
        + this.break_fix_staff_overtime_cost
        + this.break_fix_average_admin_fee_costs_for_rescheduling_exams
        + (this.calculatorMode === CalculatorHTMMode.HTM ? this.break_fix_HTM_productivity_loss : 0);

    /** Break and Fix Advanced **/
    private break_fix_examinations_transfert_lost = Math.ceil(this.break_fix_exams_loss * this.K27);
    private break_fix_examinations_transfert_cost_loss = this.break_fix_examinations_transfert_lost * this.K29
        + this.break_fix_examinations_transfert_lost
        * this.K31 * ((this.K35 * this.K37) + (this.K39 * this.K41))
        + this.break_fix_examinations_transfert_lost * this.I10;
    private break_fix_surgery_dpt_cost_loss = Math.ceil(this.break_fix_exams_loss * this.V27)
        * this.V41 * ((this.V29 * this.V31) + (this.V33 * this.V35) + (this.V37 * this.V39))
    private break_fix_intensive_care_dpt_cost_loss = Math.ceil(this.break_fix_exams_loss * this.V27) * this.V43 * this.V45
    private break_fix_patient_transfer_surgery_dpt_loss = this.break_fix_examinations_transfert_cost_loss
        + this.break_fix_surgery_dpt_cost_loss
        + this.break_fix_intensive_care_dpt_cost_loss;
    private break_fix_total_hospital_estimated_loss = this.break_fix_patient_transfer_surgery_dpt_loss + this.break_fix_estimated_Loss;

    values = {
        average_number_exam_hour: {
            value: this.average_number_exam_hour || 0,
            label: 'Average number of exam per hour',
            options: {
                round: false
            }
        },
        yearly_technologist_radiologist_wages: {
            value: this.yearly_technologist_radiologist_wages || 0,
            label: 'Yearly Technologist & Radiologist Wages',
            options: {
                displayCurrency: true
            }
        },
        average_number_exam_year: {
            value: this.average_number_exam_year || 0,
            label: 'Average number of exams per year',
        },
        estimated_revenue_year: {
            value: this.estimated_revenue_year || 0,
            label: 'Estimated revenue per year',
            options: {
                displayCurrency: true
            }
        },
        on_watch_predict_examinations_saved: {
            value: this.on_watch_predict_examinations_saved || 0,
            label: '# Examinations Saved',
            options: {
                displayCurrency: true
            }
        },
        on_watch_predict_revenue_saved: {
            value: this.on_watch_predict_revenue_saved || 0,
            label: '# Revenue Saved',
            options: {
                displayCurrency: true
            }
        },
        on_watch_predict_operating_hours_saved: {
            value: this.on_watch_predict_operating_hours_saved || 0,
            label: '# Operating hours Saved',
        },
        on_watch_predict_staff_iddle_time_cost_avoidance: {
            value: this.on_watch_predict_staff_iddle_time_cost_avoidance || 0,
            label: 'Staff idle time cost avoidance',
            options: {
                displayCurrency: true
            }
        },
        on_watch_predict_staff_overtime_cost_avoidance: {
            value: this.on_watch_predict_staff_overtime_cost_avoidance || 0,
            label: 'Staff Overtime Cost avoidance',
            options: {
                displayCurrency: true
            }
        },
        on_watch_predict_average_admin_fee_costs_avoidance_for_rescheduling_exams: {
            value: this.on_watch_predict_average_admin_fee_costs_avoidance_for_rescheduling_exams || 0,
            label: 'Average Admin Fee costs avoidance for rescheduling Exams',
            options: {
                displayCurrency: true
            }
        },
        on_watch_predict_labor_productivity_in_hours: {
            value: this.on_watch_predict_labor_productivity_in_hours || 0,
            label: 'Labor Productivity in Hours',
            options: {
                isHTMMode: true
            }
        },
        on_watch_predict_labor_productivity: {
            value: this.on_watch_predict_labor_productivity || 0,
            label: 'Labor Productivity',
            options: {
                isHTMMode: true,
                displayCurrency: true
            }
        },
        on_watch_predict_estimated_benefits: {
            value: this.on_watch_predict_estimated_benefits || 0,
            label: 'Imaging Dpt Estimated Benefits',
            options: {
                displayCurrency: true
            }
        },

        on_watch_predict_examinations_transfert_saved: {
            value: this.on_watch_predict_examinations_transfert_saved || 0,
            label: '# Examinations Transfert Saved',
            options: {
                isAdvancedMode: true
            }
        },
        on_watch_predict_examinations_transfert_cost_saved: {
            value: this.on_watch_predict_examinations_transfert_cost_saved || 0,
            label: '# Examinations Transfert Cost Saved',
            options: {
                displayCurrency: true,
                isAdvancedMode: true
            }
        },
        on_watch_predict_surgery_dpt_cost_saved: {
            value: this.on_watch_predict_surgery_dpt_cost_saved || 0,
            label: 'Surgery Dpt cost saved',
            options: {
                displayCurrency: true,
                isAdvancedMode: true
            }
        },
        on_watch_predict_intensive_care_dpt_cost_saved: {
            value: this.on_watch_predict_intensive_care_dpt_cost_saved || 0,
            label: 'Intensive Care Dpt cost saved',
            options: {
                displayCurrency: true,
                isAdvancedMode: true
            }
        },
        on_watch_predict_patient_transfer_surgery_dpt_benefits: {
            value: this.on_watch_predict_patient_transfer_surgery_dpt_benefits || 0,
            label: 'Patient Transfer + Surgery Dpt Benefits',
            options: {
                displayCurrency: true,
                isAdvancedMode: true
            }
        },
        on_watch_predict_total_hospital_estimated_benefits: {
            value: this.on_watch_predict_total_hospital_estimated_benefits || 0,
            label: 'Total Hospital Estimated Benefits',
            options: {
                displayCurrency: true,
                isAdvancedMode: true
            }
        },

        on_watch_examinations_saved: {
            value: this.on_watch_examinations_saved || 0,
            label: ' # Examinations Saved',
        },
        on_watch_revenue_saved: {
            value: this.on_watch_revenue_saved || 0,
            label: ' # Revenue Saved',
            options: {
                displayCurrency: true
            }
        },
        on_watch_operating_hours_saved: {
            value: this.on_watch_operating_hours_saved || 0,
            label: '# Operating hours Saved',
        },
        on_watch_staff_iddle_time_cost_avoidance: {
            value: this.on_watch_staff_iddle_time_cost_avoidance || 0,
            label: 'Staff idle time cost avoidance',
            options: {
                displayCurrency: true
            }
        },
        on_watch_staff_overtime_cost_avoidance: {
            value: this.on_watch_staff_overtime_cost_avoidance || 0,
            label: 'Staff Overtime Cost avoidance',
            options: {
                displayCurrency: true
            }
        },
        on_watch_average_admin_fee_costs_avoidance_for_rescheduling_exams: {
            value: this.on_watch_average_admin_fee_costs_avoidance_for_rescheduling_exams || 0,
            label: 'Average Admin Fee costs avoidance for rescheduling Exams',
            options: {
                displayCurrency: true
            }
        },
        on_watch_labor_productivity_in_hours: {
            value: this.on_watch_labor_productivity_in_hours || 0,
            label: 'Labor Productivity in Hours',
            options: {
                isHTMMode: true
            }
        },
        on_watch_labor_productivity: {
            value: this.on_watch_labor_productivity || 0,
            label: 'Labor Productivity',
            options: {
                isHTMMode: true,
                displayCurrency: true
            }
        },
        on_watch_estimated_benefits: {
            value: this.on_watch_estimated_benefits || 0,
            label: 'Imaging Dpt Estimated Benefits',
            options: {
                displayCurrency: true
            }
        },

        on_watch_examinations_transfert_saved: {
            value: this.on_watch_examinations_transfert_saved || 0,
            label: '# Examinations Transfert Saved',
            options: {
                isAdvancedMode: true
            }
        },
        on_watch_examinations_transfert_cost_saved: {
            value: this.on_watch_examinations_transfert_cost_saved || 0,
            label: '# Examinations Transfert Cost Saved',
            options: {
                displayCurrency: true,
                isAdvancedMode: true
            }
        },
        on_watch_surgery_dpt_cost_saved: {
            value: this.on_watch_surgery_dpt_cost_saved || 0,
            label: 'Surgery Dpt cost saved',
            options: {
                displayCurrency: true,
                isAdvancedMode: true
            }
        },
        on_watch_intensive_care_dpt_cost_saved: {
            value: this.on_watch_intensive_care_dpt_cost_saved || 0,
            label: 'Intensive Care Dpt cost saved',
            options: {
                displayCurrency: true,
                isAdvancedMode: true
            }
        },
        on_watch_patient_transfer_surgery_dpt_benefits: {
            value: this.on_watch_patient_transfer_surgery_dpt_benefits || 0,
            label: 'Patient Transfer + Surgery Dpt Benefits',
            options: {
                displayCurrency: true,
                isAdvancedMode: true
            }
        },
        on_watch_total_hospital_estimated_benefits: {
            value: this.on_watch_total_hospital_estimated_benefits || 0,
            label: 'Total Hospital Estimated Benefits',
            options: {
                displayCurrency: true,
                isAdvancedMode: true
            }
        },

        break_fix_exams_loss: {
            value: this.break_fix_exams_loss || 0,
            label: 'Break & Fix Exams loss',
        },
        break_fix_revenue_loss: {
            value: this.break_fix_revenue_loss || 0,
            label: 'Break & Fix revenue Loss',
            options: {
                displayCurrency: true
            }
        },
        break_fix_operating_hours_lost: {
            value: this.break_fix_operating_hours_lost || 0,
            label: '# Operating hours Lost',
        },
        break_fix_staff_cost_due_to_iddle_time: {
            value: this.break_fix_staff_cost_due_to_iddle_time || 0,
            label: 'Staff cost due to idle time',
            options: {
                displayCurrency: true
            }
        },
        break_fix_staff_overtime_cost: {
            value: this.break_fix_staff_overtime_cost || 0,
            label: 'Staff Overtime cost',
            options: {
                displayCurrency: true
            }
        },
        break_fix_average_admin_fee_costs_for_rescheduling_exams: {
            value: this.break_fix_average_admin_fee_costs_for_rescheduling_exams || 0,
            label: 'Average Admin Fee costs for rescheduling Exams',
            options: {
                displayCurrency: true
            }
        },
        break_fix_labor_productivity_in_hours: {
            value: this.break_fix_HTM_productivity_loss_in_hours || 0,
            label: 'Labor Productivity in Hours',
            options: {
                isHTMMode: true
            }
        },
        break_fix_labor_productivity: {
            value: this.break_fix_HTM_productivity_loss || 0,
            label: 'Labor Productivity',
            options: {
                isHTMMode: true
            }
        },
        break_fix_estimated_Loss: {
            value: this.break_fix_estimated_Loss || 0,
            label: 'Imaging Dpt Estimated Loss',
            options: {
                displayCurrency: true
            }
        },

        break_fix_examinations_transfert_lost: {
            value: this.break_fix_examinations_transfert_lost || 0,
            label: '# Examinations Transfert lost',
            options: {
                isAdvancedMode: true
            }
        },
        break_fix_examinations_transfert_cost_loss: {
            value: this.break_fix_examinations_transfert_cost_loss || 0,
            label: '# Examinations Transfert Cost Loss',
            options: {
                displayCurrency: true,
                isAdvancedMode: true
            }
        },
        break_fix_surgery_dpt_cost_loss: {
            value: this.break_fix_surgery_dpt_cost_loss || 0,
            label: 'Surgery Dpt cost  loss',
            options: {
                displayCurrency: true,
                isAdvancedMode: true
            }
        },
        break_fix_intensive_care_dpt_cost_loss: {
            value: this.break_fix_intensive_care_dpt_cost_loss || 0,
            label: 'Intensive Care Dpt cost  loss',
            options: {
                displayCurrency: true,
                isAdvancedMode: true
            }
        },
        break_fix_patient_transfer_surgery_dpt_loss: {
            value: this.break_fix_patient_transfer_surgery_dpt_loss || 0,
            label: 'Patient Transfer + Surgery Dpt Loss',
            options: {
                displayCurrency: true,
                isAdvancedMode: true
            }
        },
        break_fix_total_hospital_estimated_loss: {
            value: this.break_fix_total_hospital_estimated_loss || 0,
            label: 'Total Hospital Estimated Loss',
            options: {
                displayCurrency: true,
                isAdvancedMode: true
            }
        },
    } satisfies CalculatorOutputType;

}
